import { MapPinIcon, MagnifyingGlassIcon, XMarkIcon } from "@heroicons/react/24/outline";
import React, { useState, useEffect, useRef, FC, useMemo } from "react";
import { useClient } from "ClientContext";
import { useGetLocations } from "hooks/useLocations";

interface Props {
  onClick?: () => void;
  onChange?: (value: string) => void;
  className?: string;
  defaultValue?: string;
  headingText?: string;
}

const LocationInput: FC<Props> = ({
  onChange = () => {},
  className = "",
  headingText = "Where to?",
}) => {
  const { setGlobal_search, global_searh } = useClient();
  const { locations: Locations,loading } = useGetLocations();

  const [value, setValue] = useState<string>(
    global_searh?.params?.location?.city || ""
  );
  const [showList, setShowList] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);

  // Keep local value in sync with global_search
  useEffect(() => {
    setValue(global_searh?.params?.location?.city || "");
  }, [global_searh?.params?.location?.city]);

  // Filter locations based on input
  const filteredLocations = useMemo(
    () =>
      Locations.filter((item) =>
        item.city.toLowerCase().includes(value.trim().toLowerCase())
      ),
    [Locations, value]
  );

  const handleSelectLocation = (
    city: string,
    state: string,
    country: string
  ) => {
    setValue(city);
    onChange?.(city);
    setGlobal_search((prevState) => ({
      ...prevState,
      params: {
        ...prevState.params,
        location: {
          city,
          state,
          country,
        },
      },
    }));
    setShowList(false);
  };

  const handleClearLocation = () => {
    setValue("");
    onChange?.("");
    setGlobal_search((prevState) => ({
      ...prevState,
      params: {
        ...prevState.params,
        location: { city: "", state: "", country: "" },
      },
    }));
  };

  const handleFocus = () => {
    setShowList(true);
  };

  const handleClickOutside = (event: MouseEvent) => {
    if (
      containerRef.current &&
      !containerRef.current.contains(event.target as Node)
    ) {
      setShowList(false);
    }
  };

  useEffect(() => {
    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const newValue = e.currentTarget.value;
    setValue(newValue);
    onChange?.(newValue);
  };

  const renderSearchValues = () => (
    <>  
      <p className="block font-semibold text-base">Destinations</p>
      <div className="mt-3 max-h-60 overflow-auto">
        {filteredLocations.length > 0 ? (
          filteredLocations.map((item, index) => (
            <div
              className="py-2 mb-1 flex items-center space-x-1 text-sm cursor-pointer flex-wrap"
              onClick={() =>
                handleSelectLocation(
                  item.city,
                  item.state || "",
                  item.country || ""
                )
              }
              key={index}
            >
              <MapPinIcon className="w-5 h-5 text-neutral-500 dark:text-neutral-400 mr-1" />
              <span className="truncate max-w-[100px]">{item.city}</span>
              <span>,</span>
              <span className="truncate max-w-[100px]">{item.state}</span>
              <span>,</span>
              <span className="truncate max-w-[100px]">{item.country}</span>
            </div>
          ))
        ) : (
          <p className="py-2 text-sm text-neutral-500">No results found</p>
        )}
      </div>
    </>
  );
if (loading) {
    return <div className="p-5">Loading...</div>;
  }
  return (
    <div className={`${className}`} ref={containerRef}>
      <div className="p-5">
        <span className="block font-semibold text-xl sm:text-2xl">
          {headingText}
        </span>
        <div className="relative mt-5 flex items-center">
          <input
            className="block w-full bg-transparent border px-4 py-3 pr-12 border-neutral-900 dark:border-neutral-200 rounded-xl focus:ring-0 focus:outline-none text-base leading-none placeholder-neutral-500 dark:placeholder-neutral-300 font-bold"
            placeholder="Search destinations"
            value={value}
            onFocus={handleFocus}
            onChange={handleChange}
          />
          {value && (
            <button
              className="absolute right-10 top-1/2 transform -translate-y-1/2"
              onClick={handleClearLocation}
            >
              <XMarkIcon className="w-5 h-5 text-neutral-700 dark:text-neutral-400" />
            </button>
          )}
          <span className="absolute right-2.5 top-1/2 -translate-y-1/2">
            <MagnifyingGlassIcon className="w-5 h-5 text-neutral-700 dark:text-neutral-400" />
          </span>
        </div>
        {showList && <div className="mt-7">{renderSearchValues()}</div>}
      </div>
    </div>
  );
};

export default LocationInput;
